import React, { useMemo, useState, useCallback } from "react"
import Wrapper from "../../shared/components/search/Wrapper"
import Panel from "../../shared/components/search/Panel"
import { MapSlot, MapSlotSkeleton } from "../../shared/components/MapSlot"
import { ResultsBar, TransferCard, TransferCardSkeleton } from "../../shared/components/transfer"
import { LeftFiltersPanle } from "../../shared/components/leftFiltersPanel"
import { FILTER_PRICE_VARIABLES } from "../../shared/utils/variables/filters"
import {
  useGetTransMetaQuery,
  useTransPrepareBookingMutation,
} from "../../app/services/transfer/search"
import { useTransferDestinations, useTransferSearch } from "../../shared/hooks"
import { SkeletonSidebar } from "../../shared/components/SkeletonSidebar"
import { Search, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CURRENCIES } from "../../shared/utils/variables/common"
import DateCard from "../../shared/components/search/Panel/DateCard"

const PAGE_SIZE = 30

const TRANSFER_TYPE_LABELS = {
  rent: "Individual",
}

const formatTransferTypeLabel = (tt) =>
  TRANSFER_TYPE_LABELS[tt] ?? tt.charAt(0).toUpperCase() + tt.slice(1)

const defaultSidebarFilters = {
  price: {
    min: FILTER_PRICE_VARIABLES.DEFAULT_MIN,
    max: FILTER_PRICE_VARIABLES.DEFAULT_MAX,
  },
  name: "",
  cancellationPolicy: [],
  excludedTransferTypes: [],
  excludedTransportTypes: [],
}

const App = ({ isAdmin, currentUserId, locale, priceFormVersion = "v1" }) => {
  const { t } = useTranslation()

  const [filters, setFilters] = useState(defaultSidebarFilters)
  const [page, setPage] = useState(1)

  const { data: transMeta } = useGetTransMetaQuery()

  const {
    departureTerminals,
    arrivalTerminals,
    isLoading: isLoadingLocations,
    isLoadingTo,
    selectedFrom,
    selectedTo,
    fetchDefault,
    fetchSearch,
    selectDestination,
    swapDestinations,
    clearDestination,
  } = useTransferDestinations()

  const {
    dates,
    handleDatesChange,
    isRoundTrip,
    passengers,
    handlePassengersChange,
    runSearch,
    searchReturn,
    results,
    isSearching,
    handleBook,
    fetchCalendarDates,
    resetCalendarCache,
    availableDepart,
    availableRetur,
    departPriceData,
    returPriceData,
    isLoadingDates,
    currency,
    handleCurrencyChange,
  } = useTransferSearch()

  // сбрасываем кэш дат при смене пункта отправления/прибытия
  const handleSelectDestination = useCallback(
    (...args) => {
      selectDestination(...args)
      resetCalendarCache()
    },
    [selectDestination, resetCalendarCache]
  )

  const handleSwapDestinations = useCallback(() => {
    swapDestinations()
    resetCalendarCache()
  }, [swapDestinations, resetCalendarCache])

  const handleClearDestination = useCallback(
    (...args) => {
      clearDestination(...args)
      resetCalendarCache()
    },
    [clearDestination, resetCalendarCache]
  )

  const rawTransfers = results?.transfers ?? []
  const apiFilters = results?.filters ?? { transfer_types: [], transport_types: [] }
  const hasSearched = results !== undefined
  const hasApiResults = rawTransfers.length > 0

  // растягиваем фон на весь экран только когда есть что показывать
  // (сам процесс поиска или готовые результаты), иначе фон только под формой
  const isExpanded = isSearching || (hasSearched && hasApiResults)

  const filteredTransfers = useMemo(() => {
    if (!rawTransfers.length) return []

    return rawTransfers
      .map((group) => {
        let prices = group.prices ?? []

        if (filters.price.min != null) prices = prices.filter((p) => p.adult >= filters.price.min)

        if (filters.price.max != null && filters.price.max !== FILTER_PRICE_VARIABLES.DEFAULT_MAX)
          prices = prices.filter((p) => p.adult <= filters.price.max)

        if (filters.cancellationPolicy.includes("non_refundable")) {
          prices = prices.filter((p) => p.non_refundable)
        }

        if (filters.name.trim()) {
          const q = filters.name.toLowerCase()
          prices = prices.filter((p) => p.name.toLowerCase().includes(q))
        }

        return { ...group, prices }
      })
      .filter((group) => {
        if (group.prices.length === 0) return false
        if (filters.excludedTransportTypes.includes(group.transport_type_code)) return false
        if (filters.excludedTransferTypes.includes(group.transfer_type)) return false
        return true
      })
  }, [rawTransfers, filters])

  const hasResults = filteredTransfers.length > 0
  const totalPages = Math.ceil(filteredTransfers.length / PAGE_SIZE)
  const pagedTransfers = filteredTransfers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const transfersForCounts = useMemo(() => {
    if (!rawTransfers.length) return []

    return rawTransfers
      .map((group) => {
        let prices = group.prices ?? []

        if (filters.price.min != null) prices = prices.filter((p) => p.adult >= filters.price.min)

        if (filters.price.max != null && filters.price.max !== FILTER_PRICE_VARIABLES.DEFAULT_MAX)
          prices = prices.filter((p) => p.adult <= filters.price.max)

        if (filters.name.trim()) {
          const q = filters.name.toLowerCase()
          prices = prices.filter((p) => p.name.toLowerCase().includes(q))
        }

        return { ...group, prices }
      })
      .filter((group) => group.prices.length > 0)
  }, [rawTransfers, filters.price, filters.name])

  const sharedCount = useMemo(
    () => transfersForCounts.reduce((acc, g) => acc + g.prices.filter((p) => p.is_group).length, 0),
    [transfersForCounts]
  )
  const privateCount = useMemo(
    () =>
      transfersForCounts.reduce((acc, g) => acc + g.prices.filter((p) => !p.is_group).length, 0),
    [transfersForCounts]
  )
  const nonRefundableCount = useMemo(
    () =>
      transfersForCounts.reduce(
        (acc, g) => acc + g.prices.filter((p) => p.non_refundable).length,
        0
      ),
    [transfersForCounts]
  )

  const transportTypeCounts = useMemo(() => {
    const counts = {}
    transfersForCounts.forEach((g) => {
      counts[g.transport_type_code] = (counts[g.transport_type_code] ?? 0) + 1
    })
    return counts
  }, [transfersForCounts])

  const transferTypeCounts = useMemo(() => {
    const counts = {}
    transfersForCounts.forEach((g) => {
      const key = g.transfer_type ?? "other"
      counts[key] = (counts[key] ?? 0) + 1
    })
    return counts
  }, [transfersForCounts])

  const handleFilterChange = useCallback(({ key, value, overwrite = false }) => {
    setPage(1)
    setFilters((prev) => {
      const keys = key.split(".")
      if (keys.length === 1) {
        const current = prev[key]
        if (overwrite || !Array.isArray(current)) return { ...prev, [key]: value }
        const next = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value]
        return { ...prev, [key]: next }
      }
      const [parent, child] = keys
      return { ...prev, [parent]: { ...prev[parent], [child]: value } }
    })
  }, [])

  const handleResetFilters = useCallback(() => {
    setPage(1)
    setFilters(defaultSidebarFilters)
  }, [])

  const handleSearch = useCallback(
    () => runSearch(selectedFrom, selectedTo),
    [runSearch, selectedFrom, selectedTo]
  )

  const handleCalendarMonthChange = useCallback(
    (ym) => fetchCalendarDates(selectedFrom, selectedTo, ym),
    [fetchCalendarDates, selectedFrom, selectedTo]
  )

  const filterConfig = useMemo(
    () => [
      {
        type: "price",
        min: filters.price.min,
        max: filters.price.max,
        currency: rawTransfers?.[0]?.prices?.[0]?.currency ?? "USD",
        onChange: handleFilterChange,
      },
      {
        type: "name",
        value: filters.name,
        placeholder: t("admin.search_by_name"),
        onChange: handleFilterChange,
        filterKey: "name",
      },
      {
        type: "checkbox",
        filterKey: "excludedTransferTypes",
        title: t("admin.transfer_type"),
        options: apiFilters.transfer_types.map((tt) => ({
          label: formatTransferTypeLabel(tt),
          value: tt, // остаётся "rent"
          count: transferTypeCounts[tt] ?? 0,
          checked: !filters.excludedTransferTypes.includes(tt),
        })),
        onChange: handleFilterChange,
      },
      {
        type: "checkbox",
        filterKey: "excludedTransportTypes",
        title: t("admin.transport_type"),
        options: apiFilters.transport_types.map((tt) => ({
          label: tt.name,
          value: tt.code,
          count: transportTypeCounts[tt.code] ?? 0,
          checked: !filters.excludedTransportTypes.includes(tt.code),
        })),
        onChange: handleFilterChange,
      },
      {
        type: "checkbox",
        filterKey: "cancellationPolicy",
        title: t("admin.cancellation_policy"),
        options: [
          {
            label: "Non Refundable",
            value: "non_refundable",
            count: nonRefundableCount,
            checked: filters.cancellationPolicy.includes("non_refundable"),
          },
        ],
        onChange: handleFilterChange,
      },
    ],
    [
      filters,
      transferTypeCounts,
      transportTypeCounts,
      nonRefundableCount,
      handleFilterChange,
      rawTransfers,
      apiFilters,
      t,
    ]
  )

  const onCurrencyChange = useCallback(
    (code) => handleCurrencyChange(code, selectedFrom, selectedTo),
    [handleCurrencyChange, selectedFrom, selectedTo]
  )

  const returnCalendar = useMemo(
    () => ({
      DateCard,
      dates,
      availableDepart,
      availableRetur,
      departPriceData,
      returPriceData,
      onCalendarMonthChange: handleCalendarMonthChange,
    }),
    [
      dates,
      availableDepart,
      availableRetur,
      departPriceData,
      returPriceData,
      handleCalendarMonthChange,
    ]
  )

  return (
    <div className={`bg-bg-secondary p-3 sm:p-4 md:p-6 ${isExpanded ? "min-h-screen" : ""}`}>
      <div className="mb-4 sm:mb-6">
        <Wrapper>
          <Panel
            currency={currency}
            currencies={CURRENCIES}
            onCurrencyChange={onCurrencyChange}
            locale={locale}
            departureTerminals={departureTerminals}
            arrivalTerminals={arrivalTerminals}
            isLoading={isLoadingLocations}
            isLoadingTo={isLoadingTo}
            selectedFrom={selectedFrom}
            selectedTo={selectedTo}
            dates={dates}
            passengers={passengers}
            isRoundTrip={isRoundTrip}
            onSearch={fetchSearch}
            onFetchDefault={fetchDefault}
            onSelect={handleSelectDestination}
            onSwap={handleSwapDestinations}
            onClear={handleClearDestination}
            onDatesChange={handleDatesChange}
            onPassengersChange={handlePassengersChange}
            onSubmit={handleSearch}
            nationalities={transMeta?.nationalities}
            contents={{
              from_placeholder: "admin.location_placeholder",
              to_placeholder: "admin.location_placeholder",
            }}
            availableDepart={availableDepart}
            availableRetur={availableRetur}
            departPriceData={departPriceData}
            returPriceData={returPriceData}
            onCalendarMonthChange={handleCalendarMonthChange}
            priceFormVersion={priceFormVersion}
          />
        </Wrapper>
      </div>

      {hasSearched && !isSearching && (
        <div className="pb-2 px-1">
          <ResultsBar count={filteredTransfers.length} isLoading={false} />
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-start">
        {hasSearched && !isSearching && hasApiResults && (
          <LeftFiltersPanle filterConfig={filterConfig} onReset={handleResetFilters} />
        )}

        <div className="w-full lg:w-[60%] xl:w-[65%] flex flex-col gap-3 sm:gap-4">
          {isSearching ? null : !hasSearched ? null : !hasApiResults ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="text-4xl mb-4">
                <Search />
              </span>
              <p className="text-text-secondary text-lg font-medium">{t("admin.nothing_found")}</p>
              <p className="text-text-muted text-sm mt-1">{t("admin.search_adjust_hint")}</p>
            </div>
          ) : !hasResults ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="text-4xl mb-4">
                <Search />
              </span>
              <p className="text-text-secondary text-lg font-medium">{t("admin.nothing_found")}</p>
              <p className="text-text-muted text-sm mt-1">{t("admin.search_adjust_hint")}</p>
            </div>
          ) : (
            <>
              {pagedTransfers.map((group, i) => (
                <TransferCard
                  key={`${group.transfer_type}-${group.transport_type_code}-${group.name}`}
                  data={group}
                  index={i}
                  handleBook={handleBook}
                  passengers={passengers}
                  transferAdditionalOptions={transMeta?.transfer_additional_options}
                  priceFormVersion={priceFormVersion}
                  searchReturn={searchReturn}
                  returnCalendar={returnCalendar}
                />
              ))}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 rounded-button border border-app-content-faint/20 text-sm text-text-secondary disabled:opacity-disabled hover:bg-bg-secondary transition-colors"
                  >
                    ←
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1.5 rounded-button text-sm transition-colors ${
                        p === page
                          ? "bg-brand-main text-text-inverse"
                          : "border border-app-content-faint/20 text-text-secondary hover:bg-bg-secondary"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 rounded-button border border-app-content-faint/20 text-sm text-text-secondary disabled:opacity-disabled hover:bg-bg-secondary transition-colors"
                  >
                    →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {hasSearched && !isSearching && hasApiResults && (
          <div className="hidden lg:block lg:w-[40%] xl:w-[35%] sticky top-6 h-[600px] bg-surface-main rounded-card shadow-sm border border-app-content-faint/10 overflow-hidden">
            <MapSlot />
          </div>
        )}
      </div>
    </div>
  )
}

export default App
